/**
 * Live transport: talks to the salesgear-mcp orchestrator (/copilot routes).
 * Same contract as MockAgentTransport — connect(conversationId, onEvent),
 * send(clientEvent) — plus fetchTablePage for server-backed result sets.
 *
 * Enable with:
 *   localStorage.sg_copilot_live = "1"
 *   localStorage.sg_copilot_api_key = "<salesgear api key>"
 *   localStorage.sg_copilot_agent_url = "<override>"  (optional; default is
 *     the mcp.<domain> sibling of the current host — see defaultAgentUrl)
 */

import makeApi from "Utils/apiURL";
import { SERVER_URL } from "Static/constant";

export const API_KEY_STORAGE = "sg_copilot_api_key";
export const AGENT_URL_STORAGE = "sg_copilot_agent_url";

// The orchestrator lives on the mcp.<domain> sibling of whatever host serves
// the app: app.salesgearqe.com -> https://mcp.salesgearqe.com, and the same for
// staging/prod, with no per-env config. Local dev (localhost / 127.0.0.1) still
// points at the dev server on :8090. A localStorage override always wins.
const defaultAgentUrl = () => {
  const { protocol, hostname } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8090";
  }
  return `${protocol}//${hostname.replace(/^app\./, "mcp.")}`;
};

class SseAgentTransport {
  constructor() {
    this.baseUrl = (
      localStorage.getItem(AGENT_URL_STORAGE) || defaultAgentUrl()
    ).replace(/\/$/, "");
    this.apiKey = localStorage.getItem(API_KEY_STORAGE) || "";
    this.serverConversationId = null;
    this.eventSource = null;
    this.onEvent = null;
    this.cursor = 0;
    this.disconnected = false;
    this.pendingSends = [];
    this.seedHistory = null;
    this.recovering = false;
    this.folderId = null;
  }

  connect = (conversationId, onEvent, seedHistory, folderId) => {
    this.onEvent = onEvent;
    // Prior turns of a reopened chat (from localStorage) so the fresh server
    // conversation is seeded with that context; absent for a new chat.
    this.seedHistory =
      Array.isArray(seedHistory) && seedHistory.length ? seedHistory : null;
    // Sequences folder the user was browsing when they opened Copilot
    // (SG-2182) — lets create_sequence place the new sequence there.
    this.folderId = folderId || null;
    this.disconnected = false;
    this.bootstrap();
    return () => {
      this.disconnected = true;
      this.onEvent = null;
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
    };
  };

  // The credential is fetched from the logged-in session (CM mints a signed
  // sgct_ token), so no user ever pastes an API key. localStorage remains a
  // dev override only.
  ensureCredential = async () => {
    if (this.apiKey) return this.apiKey;
    const res = await makeApi({ url: `${SERVER_URL}/cm/copilot/token`, method: "GET" });
    const token = res?.data?.token;
    if (!token) {
      throw new Error("Couldn't authenticate Copilot from your session. Refresh and try again.");
    }
    this.apiKey = token;
    return token;
  };

  bootstrap = async () => {
    try {
      await this.ensureCredential();
      const res = await fetch(`${this.baseUrl}/copilot/conversation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": this.apiKey
        },
        body: JSON.stringify({
          ...(this.seedHistory ? { history: this.seedHistory } : {}),
          ...(this.folderId ? { folderId: this.folderId } : {})
        })
      });
      if (!res.ok) throw new Error(`conversation init ${res.status}`);
      const data = await res.json();
      if (this.disconnected) return;
      this.serverConversationId = data.conversationId;
      this.openStream();
      // flush anything sent while the conversation was initializing
      const queued = this.pendingSends.splice(0);
      queued.forEach(evt => this.send(evt));
    } catch (err) {
      this.emitLocal({
        type: "agent_error",
        message:
          "Couldn't start Copilot (" +
          (err?.message || "network error") +
          "). Check that the agent service on " +
          this.baseUrl +
          " is running, then start a new chat."
      });
    }
  };

  openStream = () => {
    if (this.disconnected || !this.serverConversationId) return;
    const url =
      `${this.baseUrl}/copilot/stream/${this.serverConversationId}` +
      `?since=${this.cursor}&key=${encodeURIComponent(this.apiKey)}`;
    this.eventSource = new EventSource(url);
    this.eventSource.onmessage = event => {
      if (!event.data) return;
      try {
        const evt = JSON.parse(event.data);
        if (typeof evt.ts === "number") this.cursor = evt.ts;
        if (this.onEvent) this.onEvent(evt);
      } catch (e) {
        /* malformed frame; skip */
      }
    };
    // EventSource auto-reconnects, but rebuild explicitly so the `since`
    // cursor is fresh and events are never replayed or lost.
    this.eventSource.onerror = () => {
      if (this.disconnected) return;
      this.eventSource.close();
      setTimeout(this.openStream, 2000);
    };
  };

  /**
   * Ask whether this question can be answered from the app's own APIs instead
   * of a turn. Returns an intent id or null; a failure answers null, so the
   * caller just runs the normal turn.
   */
  classifyIntent = async text => {
    try {
      const res = await fetch(`${this.baseUrl}/copilot/route`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": this.apiKey },
        body: JSON.stringify({ text })
      });
      if (!res.ok) return null;
      const body = await res.json();
      return body && body.intent ? body.intent : null;
    } catch (e) {
      return null;
    }
  };

  sendFeedback = async payload => {
    if (!this.serverConversationId) return;
    try {
      await fetch(`${this.baseUrl}/copilot/feedback/${this.serverConversationId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": this.apiKey },
        body: JSON.stringify(payload)
      });
    } catch (e) {
      /* feedback is best-effort */
    }
  };

  send = async clientEvent => {
    if (this.disconnected) return;
    if (!this.serverConversationId) {
      this.pendingSends.push(clientEvent);
      return;
    }
    try {
      const res = await fetch(
        `${this.baseUrl}/copilot/event/${this.serverConversationId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": this.apiKey
          },
          body: JSON.stringify(clientEvent)
        }
      );
      // The server-side conversation is in-memory only, so it's gone after the
      // agent restarts (deploy) or its TTL lapses. A 404 here would otherwise be
      // swallowed (fetch doesn't reject on HTTP errors) and the turn would spin
      // forever. Recreate the conversation from the seeded history and resend,
      // once, so a chat survives an agent restart transparently.
      if (res.status === 404 && !this.recovering) {
        this.pendingSends.push(clientEvent);
        await this.recreateConversation();
        return;
      }
      if (!res.ok) throw new Error(`event ${res.status}`);
    } catch (err) {
      this.recovering = false;
      this.emitLocal({
        type: "agent_error",
        message:
          "Message didn't reach the Copilot agent. Check the agent service and try again."
      });
    }
  };

  // The server-side conversation is in-memory only — gone after an agent
  // restart (deploy) or its idle TTL. Rebuild it from the seeded history so
  // the chat (and its restorable tables) survives transparently. Concurrent
  // callers wait on the one in-flight recreation instead of racing it.
  recreateConversation = async () => {
    if (this.recovering) {
      while (this.recovering) {
        await new Promise(resolve => setTimeout(resolve, 150));
      }
      return;
    }
    this.recovering = true;
    try {
      this.serverConversationId = null;
      this.cursor = 0;
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
      await this.bootstrap();
    } finally {
      this.recovering = false;
    }
  };

  // Stops the in-flight turn server-side. Fire-and-forget from the caller's
  // point of view — the UI settles once the resulting turn_end arrives over
  // the SSE stream, same as any other turn ending.
  interrupt = () => {
    if (!this.serverConversationId) return;
    fetch(`${this.baseUrl}/copilot/interrupt/${this.serverConversationId}`, {
      method: "POST",
      headers: { "x-api-key": this.apiKey }
    }).catch(() => {
      /* best-effort: if this fails the turn just runs to completion */
    });
  };

  // A 404 from the table endpoints means one of two very different things:
  // the TABLE is missing (store restores it from its persisted spec) or the
  // whole CONVERSATION was evicted (idle TTL / agent restart). For the latter,
  // rebuild the conversation first and retry once — then the table-level 404
  // flows into the normal restore path and the user just sees their data.
  conversationGone = async res => {
    if (res.status !== 404) return false;
    const body = await res
      .clone()
      .json()
      .catch(() => ({}));
    return body && body.error === "conversation_not_found";
  };

  // Server-backed table paging for the side panel.
  fetchTablePage = async (tableId, page, pageSize, retried, refresh) => {
    const res = await fetch(
      `${this.baseUrl}/copilot/table/${this.serverConversationId}/${tableId}` +
        `?page=${page}&limit=${pageSize}&key=${encodeURIComponent(this.apiKey)}` +
        `${refresh ? "&refresh=1" : ""}`
    );
    if (!retried && (await this.conversationGone(res))) {
      await this.recreateConversation();
      return this.fetchTablePage(tableId, page, pageSize, true);
    }
    if (!res.ok) throw new Error(`table page ${res.status}`);
    return res.json();
  };

  // Re-materialize a table from the spec persisted with its table_ref block:
  // a reopened chat's tables live in an evicted server conversation, so the
  // fresh conversation rebuilds them on demand and paging works again.
  restoreTable = async (tableId, spec, retried) => {
    const res = await fetch(
      `${this.baseUrl}/copilot/table/${this.serverConversationId}/${tableId}` +
        `/restore?key=${encodeURIComponent(this.apiKey)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(spec)
      }
    );
    if (!retried && (await this.conversationGone(res))) {
      await this.recreateConversation();
      return this.restoreTable(tableId, spec, true);
    }
    if (!res.ok) throw new Error(`table restore ${res.status}`);
    return res.json();
  };

  emitLocal = evt => {
    if (this.onEvent) this.onEvent({ ts: ++this.cursor, ...evt });
  };
}

export default SseAgentTransport;
